import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import FormSelect from '../../components/Forms/FormSelect';
import {
  getApplicationById,
  createApplicationParameter
} from '../../services/applications/applicationsService';
import { ApplicationDto, ParameterDto, ParameterType } from '../../services/applications/types';

const ParameterCreate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [formData, setFormData] = useState<ParameterDto>({
    applicationId: id,
    type: undefined,
    content: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchApplication(id);
    }
  }, [id, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchApplication = async (applicationId: string) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to create parameters.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getApplicationById(auth.user || null, applicationId);
      setApplication(data);
      setFormData(prev => ({
        ...prev,
        applicationId: applicationId
      }));
    } catch (err) {
      setError('Failed to fetch application details. Please try again.');
      console.error('Error fetching application:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.isAuthenticated) {
      setError('You must be authenticated to create a parameter.');
      return;
    }

    if (!id) {
      setError('Application ID is missing.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await createApplicationParameter(auth.user || null, id, formData);
      navigate(`/applications/${id}/parameters`);
    } catch (err) {
      setError('Failed to create parameter. Please try again.');
      console.error('Error creating parameter:', err);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/applications/${id}/parameters`);
  };

  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : 'Add Parameter'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: application?.name || 'Application', to: `/applications/${id}` },
          { label: "Parameters", to: `/applications/${id}/parameters` },
          { label: isLoading ? 'Loading...' : 'Add Parameter' }
        ]}
      />

      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => id && fetchApplication(id)}
            >
              Retry
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Parameter Information</h3>
                <div className="space-y-4">
                  <FormSelect
                    label="Parameter Type"
                    id="type"
                    name="type"
                    required
                    value={formData.type || ''}
                    onChange={handleInputChange}
                    options={[
                      { value: "", label: "Select parameter type" },
                      ...Object.entries(ParameterType).map(([key, value]) => ({
                        value,
                        label: key.replace(/_/g, ' ')
                      }))
                    ]}
                  />

                  <div>
                    <label
                      htmlFor="content"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Content
                    </label>
                    <textarea
                      id="content"
                      name="content"
                      required
                      value={formData.content || ''}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter parameter content"
                    />
                    {formData.type && (
                      <p className="mt-1 text-sm text-gray-500">
                        {formData.type === ParameterType.INSTRUCTION_TEXT &&
                          "System instructions that guide the AI's behavior."}
                        {formData.type === ParameterType.CHARACTER_LIMIT &&
                          "Maximum number of characters allowed."}
                        {formData.type === ParameterType.APPLICATION_KEY &&
                          "API key or secret for this application."}
                        {(formData.type === ParameterType.TELEGRAM_API_TOKEN ||
                          formData.type === ParameterType.TELEGRAM_BOT_ID) &&
                          "Credentials for Telegram integration."}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Parameter'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ParameterCreate;